import type { OperationalSummaryRow } from '../../services/informationSystemOperationalApi'

export type UnifiedStandardSnapshot = {
  standardCode: string
  selfAssessmentScore: number
  verifiedScore: number
  authority?: {
    selfAssessment?: { percent?: number; source?: string }
    verifiedAssessment?: {
      percent?: number
      status?: string
      source?: string
      verifiedControls?: number
    }
  }
  distribution?: Array<{ count?: number }>
}

export type OrgAuditReadinessInputs = {
  unifiedStandards: UnifiedStandardSnapshot[]
  orgSecurityPercent: number | null
  ready: boolean
}

export type AuditReadinessSourceType =
  | 'SELF_ASSESSMENT'
  | 'VERIFIED_AUDIT'
  | 'ORG_SECURITY_HEALTH'
  | 'NONE'

export type BlockerSeverity = 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW' | 'NONE'

export type AuditReadinessModel = {
  percent: number | null
  operationalStatus: string
  displayStatus: string
  sourceType: AuditReadinessSourceType
  sourceStandard: string | null
  blockers: number
  blockerSeverity: BlockerSeverity
}

export function normalizeStandardCode(code: string): string {
  return String(code || '')
    .toUpperCase()
    .replace(/[\s_-]+/g, '')
}

function codesMatch(a: string, b: string): boolean {
  return normalizeStandardCode(a) === normalizeStandardCode(b)
}

function scopedSnapshots(
  applicableStandards: string[],
  standards: UnifiedStandardSnapshot[]
): UnifiedStandardSnapshot[] {
  if (!applicableStandards?.length || !standards?.length) return []
  return standards.filter((s) =>
    applicableStandards.some((a) => codesMatch(a, s.standardCode))
  )
}

function hasMeaningfulSelfAssessment(snapshot: UnifiedStandardSnapshot): boolean {
  const score = Number(snapshot.selfAssessmentScore)
  if (!Number.isFinite(score) || score <= 0) return false
  const answered = (snapshot.distribution || []).reduce(
    (sum, b) => sum + Number(b?.count || 0),
    0
  )
  return answered > 0 || score > 0
}

export function deriveBlockerSeverity(row: OperationalSummaryRow): BlockerSeverity {
  const vulns = row.topVulnerabilities || []
  if (vulns.some((v) => String(v.severity).toUpperCase() === 'CRITICAL')) return 'CRITICAL'
  if (vulns.some((v) => String(v.severity).toUpperCase() === 'HIGH')) return 'HIGH'
  const blockers = row.blockerCount ?? 0
  if (blockers > 0) return 'MEDIUM'
  return 'NONE'
}

export function mapDisplayStatus(
  operationalStatus: string,
  blockers: number,
  blockerSeverity: BlockerSeverity
): string {
  if (blockers > 0 && blockerSeverity === 'CRITICAL') return 'CRITICAL'
  if (blockers > 0) return 'BLOCKED'
  const s = String(operationalStatus || 'UNKNOWN').toUpperCase()
  if (s.includes('CRITICAL')) return 'CRITICAL'
  if (s === 'READY_FOR_REVIEW' || s === 'READY') return s
  if (s.includes('NOT') || s.includes('NEEDS')) return 'NOT_READY'
  if (s === 'UNKNOWN' || s === 'NOT_SCOPED') return 'UNKNOWN'
  return s
}

export function resolveAuditReadiness(
  row: OperationalSummaryRow,
  inputs: OrgAuditReadinessInputs
): AuditReadinessModel {
  const operationalStatus = row.readiness?.readinessStatus || 'UNKNOWN'
  const blockers = row.blockerCount ?? 0
  const blockerSeverity = deriveBlockerSeverity(row)
  const displayStatus = mapDisplayStatus(operationalStatus, blockers, blockerSeverity)

  const applicableOrder = row.applicableStandards || []
  const scoped = scopedSnapshots(applicableOrder, inputs.unifiedStandards)
  const scopedByCode = new Map(scoped.map((s) => [normalizeStandardCode(s.standardCode), s]))

  /** First applicable standard (list order) with evidence-supported confirmed compliance. */
  for (const code of applicableOrder) {
    const snap = scopedByCode.get(normalizeStandardCode(code))
    if (!snap) continue
    const auth = snap.authority?.verifiedAssessment
    const isConfirmed =
      auth?.source === 'evidence_supported_controls' &&
      (Number(auth.verifiedControls ?? 0) > 0 || Number(auth.percent ?? 0) > 0)
    if (!isConfirmed) continue
    const verifiedPct = Math.round(Number(auth?.percent ?? 0))
    return {
      percent: verifiedPct,
      operationalStatus,
      displayStatus,
      sourceType: 'VERIFIED_AUDIT',
      sourceStandard: snap.standardCode,
      blockers,
      blockerSeverity,
    }
  }

  /** First applicable standard with self-assessment progress. */
  for (const code of applicableOrder) {
    const snap = scopedByCode.get(normalizeStandardCode(code))
    if (!snap || !hasMeaningfulSelfAssessment(snap)) continue
    const selfPct = Math.round(
      Number(
        (snap as { authority?: { selfAssessment?: { percent?: number } } }).authority
          ?.selfAssessment?.percent ?? snap.selfAssessmentScore,
      ),
    )
    return {
      percent: selfPct,
      operationalStatus,
      displayStatus,
      sourceType: 'SELF_ASSESSMENT',
      sourceStandard: snap.standardCode,
      blockers,
      blockerSeverity,
    }
  }

  const orgPct = inputs.orgSecurityPercent
  if (orgPct != null && Number.isFinite(orgPct) && orgPct > 0) {
    return {
      percent: Math.round(orgPct),
      operationalStatus,
      displayStatus,
      sourceType: 'ORG_SECURITY_HEALTH',
      sourceStandard: null,
      blockers,
      blockerSeverity,
    }
  }

  return {
    percent: null,
    operationalStatus,
    displayStatus: percentUnknownDisplayStatus(displayStatus),
    sourceType: 'NONE',
    sourceStandard: null,
    blockers,
    blockerSeverity,
  }
}

function percentUnknownDisplayStatus(displayStatus: string): string {
  if (displayStatus === 'BLOCKED' || displayStatus === 'CRITICAL' || displayStatus === 'NOT_READY') {
    return displayStatus
  }
  return 'UNKNOWN'
}

export function formatStandardLabel(code: string): string {
  const u = normalizeStandardCode(code)
  if (u === 'ISO27001') return 'ISO 27001'
  if (u === 'SOC2') return 'SOC 2'
  if (u === 'PCIDSS2025' || u === 'PCIDSS') return 'PCI DSS'
  return code
}
